import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Loading from '../../../components/Loading';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageHeaderActions,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

import EmployeeDocumentsSection from '../EmployeeDocumentsSection';
import EmployeeAuditHistoryTab from '../EmployeeAuditHistoryTab';

// ─── Mapeamentos de exibição ───────────────────────────────────────────────────

const EMPLOYMENT_STATUS_LABEL = {
   ATIVO:     'Ativo',
   'EXP 1':   'Em experiência - 1º mês',
   'EXP 2':   'Em experiência - 60 dias finais',
   AFASTADO:  'Afastado',
   DESLIGADO: 'Desligado',
};

const COMPANY_ENTITY_LABEL = {
   'MYSHOP COMERCIO E SERVICOS LTDA': 'MyShop Comércio e Serviços LTDA',
   'LIFE PURIFICADORES LTDA':         'Life Purificadores LTDA',
};

const TRANSPORTATION_COST_TYPE_LABEL = {
   'AUXÍLIO COMBUSTÍVEL': 'Auxílio combustível',
   'VALE TRANSPORTE':     'Vale Transporte',
   NENHUM:                'Nenhum',
   'NÃO SE APLICA':       'Não se aplica',
};

// ─── Definição das abas ───────────────────────────────────────────────────────

const TABS = [
   { key: 'dados',     label: 'Dados do funcionário'   },
   { key: 'historico', label: 'Histórico de alterações' },
];

// ─── Helpers de formatação ────────────────────────────────────────────────────

function formatDateBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month, day] = String(isoDateString).substring(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

function formatCurrencyBRL(numericValue) {
   if (numericValue === null || numericValue === undefined) return '—';
   return Number(numericValue).toLocaleString('pt-BR', {
      style:    'currency',
      currency: 'BRL',
   });
}

function calculateEmploymentDuration(hireDateString, terminationDateString, employmentStatus) {
   if (!hireDateString) return '—';

   const startDate = new Date(`${hireDateString.substring(0, 10)}T00:00:00`);
   const endDate =
      employmentStatus === 'DESLIGADO' && terminationDateString
         ? new Date(`${terminationDateString.substring(0, 10)}T00:00:00`)
         : new Date();

   let years  = endDate.getFullYear() - startDate.getFullYear();
   let months = endDate.getMonth()    - startDate.getMonth();
   let days   = endDate.getDate()     - startDate.getDate();

   if (days < 0) {
      months -= 1;
      days   += new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
   }
   if (months < 0) {
      years  -= 1;
      months += 12;
   }

   const yearsPart  = years  > 0 ? `${years} ano${years   !== 1 ? 's' : ''}`             : '';
   const monthsPart = months > 0 ? `${months} ${months    !== 1 ? 'meses' : 'mês'}`      : '';
   const daysPart   = days   > 0 ? `${days} dia${days     !== 1 ? 's' : ''}`             : '';

   return [yearsPart, monthsPart, daysPart].filter(Boolean).join(', ') || 'Menos de 1 dia';
}

// ─── Subcomponente: tab bar ───────────────────────────────────────────────────

function TabBar({ activeTabKey, onTabChange }) {
   return (
      <div
         style={{
            display:      'flex',
            gap:          '4px',
            borderBottom: '2px solid rgba(0,0,0,0.08)',
            marginBottom: '24px',
         }}
      >
         {TABS.map((tab) => {
            const isActive = tab.key === activeTabKey;
            return (
               <button
                  key={tab.key}
                  type="button"
                  onClick={() => onTabChange(tab.key)}
                  style={{
                     padding:         '10px 20px',
                     fontSize:        '14px',
                     fontWeight:      isActive ? 600 : 400,
                     color:           isActive ? 'var(--color-primary, #1565c0)' : '#6b7280',
                     background:      'transparent',
                     border:          'none',
                     borderBottom:    isActive ? '2px solid var(--color-primary, #1565c0)' : '2px solid transparent',
                     marginBottom:    '-2px',
                     cursor:          'pointer',
                     transition:      'color 0.15s, border-color 0.15s',
                     whiteSpace:      'nowrap',
                  }}
               >
                  {tab.label}
               </button>
            );
         })}
      </div>
   );
}

// ─── Subcomponente: aba de dados do funcionário ───────────────────────────────

function EmployeeDataTab({ employee, employeeId }) {
   return (
      <>
         {/* ─── Dados pessoais ───────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Dados pessoais</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid $cols={12} $colsMobile={12}>
                  <Field $col={6} $colMobile={12}>
                     <label>Nome completo</label>
                     <p>{employee.full_name}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>CPF</label>
                     <p>{employee.cpf}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Data de nascimento</label>
                     <p>{formatDateBR(employee.birth_date)}</p>
                  </Field>
                  <Field $col={4} $colMobile={12}>
                     <label>WhatsApp</label>
                     <p>{employee.whatsapp_number ?? '—'}</p>
                  </Field>
                  <Field $col={4} $colMobile={12}>
                     <label>E-mail</label>
                     <p>{employee.email ?? '—'}</p>
                  </Field>
                  <Field $col={4} $colMobile={12}>
                     <label>Chave PIX</label>
                     <p>{employee.pix_key ?? '—'}</p>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ─── Dados contratuais ────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Dados contratuais</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid $cols={12} $colsMobile={12}>
                  <Field $col={8} $colMobile={12}>
                     <label>Cargo</label>
                     <p>{employee.jobPosition?.position_name ?? '—'}</p>
                  </Field>
                  <Field $col={4} $colMobile={6}>
                     <label>Tempo de empresa</label>
                     <p>
                        {calculateEmploymentDuration(
                           employee.hire_date,
                           employee.termination_date,
                           employee.employment_status,
                        )}
                     </p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Empresa</label>
                     <p>{COMPANY_ENTITY_LABEL[employee.company_entity] ?? '—'}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Status</label>
                     <p>{EMPLOYMENT_STATUS_LABEL[employee.employment_status] ?? '—'}</p>
                  </Field>
                  <Field $col={6} $colMobile={12}>
                     <label>Departamento</label>
                     <p>{employee.department?.department_name ?? '—'}</p>
                  </Field>
                  <Field $col={6} $colMobile={12}>
                     <label>Centro de custo</label>
                     <p>
                        {employee.costCenter
                           ? `${employee.costCenter.cost_center_code} — ${employee.costCenter.cost_center_name}`
                           : '—'}
                     </p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Praça / Cidade</label>
                     <p>{employee.city_location ?? '—'}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Carga horária mensal</label>
                     <p>{employee.monthly_work_hours ? `${employee.monthly_work_hours}h` : '—'}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Data de admissão</label>
                     <p>{formatDateBR(employee.hire_date)}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Data de desligamento</label>
                     <p>{formatDateBR(employee.termination_date)}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Salário base</label>
                     <p>{formatCurrencyBRL(employee.base_salary)}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Transporte</label>
                     <p>{TRANSPORTATION_COST_TYPE_LABEL[employee.transportation_cost_type] ?? '—'}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Vale alimentação</label>
                     <p>{employee.has_meal_allowance ? 'Sim' : 'Não'}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Técnico de campo</label>
                     <p>{employee.is_technician ? 'Sim' : 'Não'}</p>
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Vendedor</label>
                     <p>{employee.is_salesperson ? 'Sim' : 'Não'}</p>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ─── Seção de documentos──────────────────────── */}
         <EmployeeDocumentsSection employeeId={employeeId} />
      </>
   );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function EmployeeShow() {
   const { employeeId } = useParams();
   const navigate       = useNavigate();

   const [employee, setEmployee]       = useState(null);
   const [isLoading, setIsLoading]     = useState(true);
   const [activeTabKey, setActiveTabKey] = useState('dados');

   useEffect(() => {
      async function fetchEmployee() {
         setIsLoading(true);
         try {
            const response = await axios.get(`/hr/employees/${employeeId}`);
            setEmployee(response.data?.data?.employee ?? null);
         } catch (error) {
            const errorMessage =
               error?.response?.data?.errors?.[0] ??
               error?.message ??
               'Erro ao carregar dados do funcionário.';
            toast.error(errorMessage);
         } finally {
            setIsLoading(false);
         }
      }
      fetchEmployee();
   }, [employeeId]);

   if (isLoading) return <Loading isLoading />;

   if (!employee) {
      return (
         <Container>
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Funcionário não encontrado</PageTitle>
                  <PageSubtitle>
                     O registro solicitado não existe ou foi removido.
                  </PageSubtitle>
               </PageHeaderText>
               <PageHeaderActions>
                  <button type="button" onClick={() => navigate('/hr/employees')}>
                     ← Voltar para a lista
                  </button>
               </PageHeaderActions>
            </PageHeader>
         </Container>
      );
   }

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>{employee.full_name}</PageTitle>
               <PageSubtitle>
                  {COMPANY_ENTITY_LABEL[employee.company_entity] ?? employee.company_entity}
                  {employee.jobPosition?.position_name ? ` · ${employee.jobPosition.position_name}` : ''}
                  {' · '}
                  {EMPLOYMENT_STATUS_LABEL[employee.employment_status] ?? employee.employment_status}
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <button type="button" onClick={() => navigate('/hr/employees')}>
                  ← Voltar
               </button>
               <button
                  type="button"
                  onClick={() => navigate(`/hr/employees/${employeeId}/edit`)}
               >
                  Editar
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Tab bar ──────────────────────────────────────────────── */}
         <TabBar activeTabKey={activeTabKey} onTabChange={setActiveTabKey} />

         {/* ─── Conteúdo da aba ativa ────────────────────────────────── */}
         {activeTabKey === 'dados' && (
            <EmployeeDataTab employee={employee} employeeId={employeeId} />
         )}
         {activeTabKey === 'historico' && (
            <EmployeeAuditHistoryTab employeeId={employeeId} />
         )}
      </Container>
   );
}
